import { useMemo } from 'react';
import {
  AnnotatedCommentThread,
  useCreateFileComment,
  useDeleteFileComment,
  useResolveFileComment,
  useUpdateFileComment,
} from '@/features/comment-on-file';
import type {
  AnnotatedCommentMediaKind,
  CommentData,
  CommentThreadActions,
} from '@/features/comment-on-file';
import type { MentionOption } from '@/entities/project-comment';

interface Props {
  comment: CommentData;
  assetKind: AnnotatedCommentMediaKind;
  depth?: number;
  mentionOptions?: MentionOption[];
}

/**
 * Asset (file) comment thread. Thin wrapper that wires the FILE action hooks
 * into `AnnotatedCommentThread` from the feature layer. Annotation replay,
 * video timecode, and the annotation toolbar are intrinsic to that thread.
 * Must render inside a <MediaPlayerProvider> (mounted by AssetDetail /
 * AssetComparePane).
 */
export function CommentThread({ comment, assetKind, depth, mentionOptions }: Props) {
  const { createComment } = useCreateFileComment();
  const { updateComment } = useUpdateFileComment();
  const { deleteComment, isDeleting } = useDeleteFileComment();
  const { resolveComment } = useResolveFileComment();

  const actions = useMemo<CommentThreadActions>(
    () => ({
      createComment: async (content, options) => {
        await createComment(content, options);
      },
      updateComment,
      deleteComment,
      isDeleting,
      resolveComment,
    }),
    [createComment, updateComment, deleteComment, isDeleting, resolveComment],
  );

  return (
    <AnnotatedCommentThread
      comment={comment}
      assetKind={assetKind}
      depth={depth}
      actions={actions}
      mentionOptions={mentionOptions}
    />
  );
}
